import { z } from "zod";

import { isAdwSchema } from "@/src/models/common-schema";
import { LegalDocumentType } from "@/src/models/constants/document-types";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { ExtractedDocumentFields } from "@/src/models/documents/extracted-document-fields";
import { locationSchema } from "@/src/models/location";
import { patientMinimalSchema } from "@/src/models/patient";
import { practitionerSchema } from "@/src/models/practitioner";

export const legalTypeDisplay: Record<LegalDocumentType, string> = {
  [LegalDocumentType.ADVANCED_CARE_DIRECTIVE]: "Advanced Care Directive",
  [LegalDocumentType.COURT_DOCUMENTS]: "Court Documents",
  [LegalDocumentType.GRIEVANCE]: "Grievance",
  [LegalDocumentType.LEGAL]: "Legal",
  [LegalDocumentType.POWER_OF_ATTORNEY]: "Power of Attorney",
  [LegalDocumentType.SUBPOENA]: "Subpoena",
};

const baseLegalFields = {
  patient: patientMinimalSchema,
  provider: practitionerSchema.nullable(),
  receivingLocation: locationSchema.nullable(),
};

const extendedLegalSchema = z.object({
  isAdw: isAdwSchema,
  ...DocumentDateSchema.shape,
});

const legalCustomFields: Record<LegalDocumentType, Record<string, z.ZodTypeAny>> = {
  [LegalDocumentType.ADVANCED_CARE_DIRECTIVE]: extendedLegalSchema.pick({ isAdw: true, dateSigned: true }).shape,
  [LegalDocumentType.COURT_DOCUMENTS]: extendedLegalSchema.pick({ dateReceived: true }).shape,
  [LegalDocumentType.GRIEVANCE]: extendedLegalSchema.pick({}).shape,
  [LegalDocumentType.LEGAL]: extendedLegalSchema.shape,
  [LegalDocumentType.POWER_OF_ATTORNEY]: extendedLegalSchema.pick({ isAdw: true, dateSigned: true }).shape,
  [LegalDocumentType.SUBPOENA]: extendedLegalSchema.pick({ dateReceived: true }).shape,
};

const makeLegalSchema = (type: LegalDocumentType, customFields: Record<string, z.ZodTypeAny>) =>
  z.object({
    type: z.literal(type),
    ...baseLegalFields,
    ...customFields,
  });

export const legalSchemas: Record<LegalDocumentType, z.ZodObject<any>> = (
  Object.values(LegalDocumentType) as LegalDocumentType[]
).reduce(
  (acc, type) => {
    acc[type] = makeLegalSchema(type, legalCustomFields[type]);

    return acc;
  },
  {} as Record<LegalDocumentType, z.ZodObject<any>>
);

const legalSchemasArray = (Object.values(LegalDocumentType) as LegalDocumentType[]).map(
  (type) => legalSchemas[type]
) as [z.ZodObject<any>, ...z.ZodObject<any>[]];

export const anyLegalSchema = z.discriminatedUnion("type", legalSchemasArray);

/** See {@link ExtractedDocumentFields} for why the fields are reconstructed rather than inferred. */
export type Legal = ExtractedDocumentFields<LegalDocumentType, typeof extendedLegalSchema, typeof baseLegalFields> &
  DatabaseDefaults;

export const legalShape: Legal = {
  id: "",
  patient: null,
  provider: null,
  receivingLocation: null,
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
  groupId: "",
  type: LegalDocumentType.POWER_OF_ATTORNEY,
  dateOfDocument: null,
  dateReceived: null,
  dateSigned: null,
  dateOfEvent: null,
};
